import { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Package } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CreateReservationModal from '../components/CreateReservationModal';
import Header from '../components/Header';
import { ProductThumbnail } from '../components/ui/aspect-ratio-image';
import { Button } from '../components/ui/button';
import { DataTable } from '../components/ui/data-table';
import { Input } from '../components/ui/input';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import type { AssetTemplate } from '../types/assetTemplate';
import type { StockReservationLineInput } from '../types/stockReservation';
import { getFirstProductImageUrl } from '../utils/productImage';

type Role = { key: string; name: string; description: string };
type User = { id: string; email: string; first_name: string; last_name: string; roles: Role[] };

const availableCount = (product: AssetTemplate) => Number(product.available_units_count ?? 0);

export default function ReservationNew() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const presetIds = (params.get('instanceIds') || '').split(',').filter(Boolean);
  const reservationId = params.get('reservationId') || undefined;
  const adding = Boolean(reservationId);
  const [user, setUser] = useState<User | null>(null);
  const [products, setProducts] = useState<AssetTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [qtyById, setQtyById] = useState<Record<string, number>>({});
  const [reserveOpen, setReserveOpen] = useState(presetIds.length > 0);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('user');
      if (saved) setUser(JSON.parse(saved));
    } catch {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    const q = new URLSearchParams({
      has_available_units: 'true',
      page: String(page),
      limit: String(limit),
      sortBy: 'name',
      sortOrder: 'ASC',
    });
    if (debouncedSearch) q.set('search', debouncedSearch);
    fetch(`/api/asset-templates?${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((data) => {
        if (!data.success) return;
        setProducts(data.data);
        setTotal(data.pagination.total);
        setPages(data.pagination.pages);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [page, limit, debouncedSearch]);

  const setQty = (id: string, raw: string, max: number) => {
    const next = Math.floor(Number(raw));
    if (!Number.isFinite(next) || next <= 0) {
      setQtyById((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      return;
    }
    setQtyById((prev) => ({ ...prev, [id]: Math.min(next, max) }));
  };

  const items: StockReservationLineInput[] = useMemo(
    () => Object.entries(qtyById)
      .filter(([, quantity]) => quantity > 0)
      .map(([template_id, quantity]) => ({ template_id, quantity })),
    [qtyById],
  );
  const totalUnits = items.reduce((sum, line) => sum + line.quantity, 0);

  const columns: ColumnDef<AssetTemplate>[] = [
    {
      accessorKey: 'name',
      header: 'Product',
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="flex items-center gap-3">
            <ProductThumbnail
              src={getFirstProductImageUrl(product.images)}
              alt={product.name}
              size="sm"
              fallback={<Package className="h-6 w-6 text-muted-foreground" />}
            />
            <span className="font-medium text-gray-900">{product.name}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'template_code',
      header: 'Code',
      cell: ({ row }) => (
        <span className="font-mono text-sm text-muted-foreground">{row.original.template_code || '-'}</span>
      ),
    },
    {
      id: 'available',
      header: 'Available',
      cell: ({ row }) => <span className="font-medium">{availableCount(row.original)}</span>,
    },
    {
      id: 'qty',
      header: 'Reserve',
      enableSorting: false,
      cell: ({ row }) => {
        const max = availableCount(row.original);
        return (
          <Input
            type="number"
            min={0}
            max={max}
            className="w-20"
            value={qtyById[row.original.id] ?? ''}
            placeholder="0"
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setQty(row.original.id, e.target.value, max)}
          />
        );
      },
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="reservations" />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(reservationId ? `/reservations/${reservationId}` : '/reservations')}
            className="h-8 w-8 shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-heading font-bold text-primary truncate">
            {adding ? 'Add products' : 'New Reservation'}
          </h1>
          <Button
            className="ml-auto shrink-0"
            disabled={!totalUnits}
            onClick={() => setReserveOpen(true)}
          >
            {adding ? `Add (${totalUnits})` : `Reserve (${totalUnits})`}
          </Button>
        </div>
        <DataTable
          columns={columns}
          data={products}
          loading={loading}
          searchKey="name"
          searchPlaceholder="Search products..."
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          serverSide
          pagination={{ total, page, limit, pages }}
          onPaginationChange={(newPage, newLimit) => {
            setPage(newPage);
            setLimit(newLimit);
          }}
          emptyMessage="No products with available warehouse stock."
        />
      </main>
      <CreateReservationModal
        open={reserveOpen}
        instanceIds={presetIds}
        items={presetIds.length ? [] : items}
        reservationId={reservationId}
        onOpenChange={setReserveOpen}
      />
    </div>
  );
}
